/**
 * NoticeReview.js — what the nightly watch found, waiting for a yes or a no
 * =========================================================================
 * Brian, 20 October 2026: *"Lets build an API or something that checks emails
 * or something on a nightly basis to capture these sorts of things and I can
 * review and confirm in the morning."*
 *
 * Every card is one notice the watch staged overnight from the mail or the
 * deal folders, and none of them is on the book. Each card says, before the
 * button that commits it:
 *
 *   - the company, what happened and the date (§0.2 — no row ids as names);
 *   - the figures as the document states them, and the price they imply;
 *   - where it was found, with a link to the email or the file;
 *   - anything the reading could not settle, and anything the watch noticed
 *     (another notice with the same principal on the same deal);
 *   - exactly what Confirm will write: the lot, its cost, the note it draws
 *     on — the server's own dry run, not a summary of it (§1).
 *
 * Confirm posts that one notice. Reject asks why and marks it must-not-post.
 * Both go through `securities/notice-review/<id>/<action>/`, which refuses
 * anything the watch is not holding, so this page cannot post a notice nobody
 * reviewed here.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { Badge, Button, Card, EmptyState, Modal, PageHeader, TextArea } from "../components/ui";
import t from "../components/theme";

const WHAT = {
  CONVERSION: "conversion",
  ELOC_ADVANCE: "equity line settlement",
  WARRANT_EXERCISE: "warrant exercise",
};

export function longDate(value) {
  if (!value) return "undated";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

export function amount(value, currency = "USD") {
  if (value === null || value === undefined || value === "") return "n/a";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${currency || "USD"} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function count(value) {
  if (value === null || value === undefined || value === "") return "n/a";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

export function price(value) {
  if (value === null || value === undefined || value === "") return "n/a";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString("en-US", { maximumFractionDigits: 6 });
}

export function headline(row) {
  const what = WHAT[row.notice_type] || String(row.notice_type || "notice").toLowerCase();
  const company = row.company || "An issuer the watch could not identify";
  return `${company} — ${what} of ${longDate(row.notice_date)}`;
}

function Figure({ label, value, sub }) {
  return (
    <div style={{ minWidth: 130 }}>
      <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: t.text, fontFamily: t.fontMono, whiteSpace: "nowrap" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: t.textMuted }}>{sub}</div>}
    </div>
  );
}

function Notes({ tone, title, items }) {
  if (!items || !items.length) return null;
  const palette = tone === "danger"
    ? { bg: t.dangerBg, border: t.dangerBorder, color: t.danger }
    : { bg: t.warningBg, border: t.warningBorder, color: t.warning };
  return (
    <div style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: t.radiusSm, padding: "10px 12px", marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: palette.color, marginBottom: 4 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: t.textSecondary, lineHeight: 1.5 }}>
        {items.map((w, i) => <li key={i}>{w}</li>)}
      </ul>
    </div>
  );
}

/** What Confirm will write, from the server's dry run. */
export function WouldPost({ preview, currency }) {
  if (!preview) return null;
  if (preview.refused) {
    return <Notes tone="danger" title="Posting would refuse this notice" items={[preview.refused]} />;
  }
  const p = preview.would_post || {};
  const d = p.drawdown;
  const lines = [
    `A lot of ${count(p.quantity)} ${p.security || "shares"} dated ${longDate(p.trade_date)}, costing ${amount(p.cost_basis, currency)} (${price(p.unit_cost)} a share).`,
  ];
  if (d) {
    lines.push(
      `Draws ${amount(d.principal_drawn, currency)} of face off the note` +
      (Number(d.principal_uncovered) > 0 ? `, and ${amount(d.principal_uncovered, currency)} that no note lot on the book can cover` : "") +
      `; releases ${amount(p.carrying_released, currency)} of carrying value and earns ${amount(p.discount_earned, currency)} of discount.`
    );
  }
  return (
    <div style={{ background: t.bgSubtle, border: `1px solid ${t.borderLight}`, borderRadius: t.radiusSm, padding: "10px 12px", marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.text, marginBottom: 4 }}>Confirming writes</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: t.textSecondary, lineHeight: 1.5 }}>
        {lines.map((line, i) => <li key={i}>{line}</li>)}
      </ul>
    </div>
  );
}

/** The broker sales that post once this notice is confirmed (D-281). */
function AlsoPosts({ then }) {
  if (!then || !then.length) return null;
  return (
    <div style={{ background: t.successBg, border: `1px solid ${t.successBorder}`, borderRadius: t.radiusSm, padding: "10px 12px", marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.text, marginBottom: 4 }}>Confirming it also posts</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: t.textSecondary, lineHeight: 1.5 }}>
        {then.map((s, i) => <li key={i}>{s.what}, {longDate(s.date)}</li>)}
      </ul>
    </div>
  );
}

export function NoticeCard({ row, onDone, then }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [why, setWhy] = useState("");
  const currency = row.currency || "USD";
  const hasWarnings = (row.warnings || []).length > 0;
  const refused = row.preview && row.preview.refused;

  const act = async (action, body) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/securities/notice-review/${row.notice_id}/${action}/`, { commit: true, ...body });
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => {
    const p = row.preview?.would_post || {};
    const ok = window.confirm(
      `Post ${count(p.quantity)} ${p.security || "shares"} at a cost of ${amount(p.cost_basis, currency)}` +
      (hasWarnings ? `, accepting ${row.warnings.length} warning(s)` : "") + "?"
    );
    if (ok) act("confirm", { accept_warnings: hasWarnings });
  };

  const found = row.found;
  return (
    <Card
      title={headline(row)}
      subtitle={[row.deal, row.instrument].filter(Boolean).join(" · ") || "not resolved to a deal"}
      action={<Badge text={found?.source === "mail" ? "found in mail" : "found in the deal folders"} variant={found?.source === "mail" ? "blue" : "gray"} />}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
        <Figure label="Principal" value={amount(row.principal, currency)} />
        {row.interest && Number(row.interest) !== 0 && <Figure label="Interest" value={amount(row.interest, currency)} />}
        <Figure label="Shares" value={count(row.shares)} />
        <Figure label="Stated price" value={price(row.price)} sub={row.implied_price ? `implies ${price(row.implied_price)}` : null} />
      </div>

      {found && (
        <div style={{ fontSize: 12.5, color: t.textSecondary, marginTop: 12 }}>
          {found.source === "mail"
            ? <>In {found.mailbox}'s mail: “{found.subject}” from {found.sender}, {longDate(found.received_at)}. </>
            : <>In the deal folders: {found.filename}. </>}
          <a href={found.link} target="_blank" rel="noreferrer" style={{ color: t.accent }}>
            {found.source === "mail" ? "Open the email" : "Open the file"}
          </a>
          <span style={{ color: t.textMuted }}> · {row.title}</span>
        </div>
      )}

      <Notes tone="warning" title="The watch noticed" items={row.watch_notes} />
      <Notes tone="danger" title="The reading could not settle" items={row.warnings} />
      <WouldPost preview={row.preview} currency={currency} />
      <AlsoPosts then={then} />

      {error && <div style={{ color: t.danger, fontSize: 12.5, marginTop: 10 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <Button variant="success" onClick={confirm} disabled={busy || Boolean(refused)}
                title={refused ? "posting would refuse it — see above" : ""}>
          {hasWarnings ? "Confirm over these warnings and post" : "Confirm and post"}
        </Button>
        <Button variant="secondary" onClick={() => setRejecting(true)} disabled={busy}>Reject</Button>
        <span style={{ fontSize: 11, color: t.textMuted, alignSelf: "center" }}>notice {row.notice_id}</span>
      </div>

      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title="Reject this notice"
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setRejecting(false)}>Cancel</Button>
            <Button variant="danger" disabled={!why.trim() || busy}
                    onClick={() => { setRejecting(false); act("reject", { why }); }}>
              Reject — must not post
            </Button>
          </div>
        }
      >
        <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 10 }}>
          {headline(row)}. It will be kept in the register, marked must-not-post, with your reason.
        </div>
        <TextArea label="Why" value={why} onChange={(e) => setWhy(e.target.value)}
                  placeholder="a superseded draft · already booked by hand · not ours" />
      </Modal>
    </Card>
  );
}

/**
 * Notices our own people sent more than once in one thread. The register holds a
 * document once, so a second conversion on the same form looks like a copy: SHF
 * Holdings' four conversions of 68 preferred on 21 October 2026 went in as one
 * (D-273 §10). Nothing here can tell a twin from a re-send, so the page asks.
 */
export function SentAgain({ rows, onDone }) {
  if (!rows || !rows.length) return null;
  return (
    <Card title={`Sent more than once in one thread (${rows.length})`}
          subtitle="The register holds each of these once. If every message was a separate conversion on the same form, the rest are not on the book. Each stays here until somebody answers it.">
      {rows.map((row, i) => (
        <SentAgainRow key={`${row.thread_id}-${row.fingerprint}`} row={row} first={i === 0} onDone={onDone} />
      ))}
    </Card>
  );
}

/**
 * One group, and the answer to it. Until 27 October 2026 a group left this list
 * four days after its messages arrived, answered or not, and SHF Holdings'
 * three further conversions of 21 October 2026 left with it (D-278).
 */
function SentAgainRow({ row, first, onDone }) {
  const [answering, setAnswering] = useState(false);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const answer = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/securities/notice-review/sent-again/answer/", {
        fingerprint: row.fingerprint, thread_id: row.thread_id, why, commit: true,
      });
      setAnswering(false);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "10px 0", borderTop: first ? "none" : `1px solid ${t.borderLight}` }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Badge text={`sent ${row.times} times`} variant="amber" />
        <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{headline(row)}</span>
      </div>
      <div style={{ fontSize: 12.5, color: t.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
        {amount(row.principal, row.currency)} into {count(row.shares)} shares at {price(row.price)} each time.
        {" "}Sent by {(row.senders || []).join(", ") || "?"} in “{row.subject}”, last on {longDate(row.last_sent)}.
        {" "}<a href={row.link} target="_blank" rel="noreferrer" style={{ color: t.accent }}>Open the thread</a>
      </div>
      {error && <div style={{ color: t.danger, fontSize: 12.5, marginTop: 6 }}>{error}</div>}
      <div style={{ marginTop: 8 }}>
        <Button variant="outline" onClick={() => setAnswering(true)} disabled={busy}>Answer</Button>
      </div>
      <Modal
        open={answering}
        onClose={() => setAnswering(false)}
        title="What were these messages?"
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setAnswering(false)}>Cancel</Button>
            <Button disabled={!why.trim() || busy} onClick={answer}>Save the answer</Button>
          </div>
        }
      >
        <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 10 }}>
          {headline(row)}, sent {row.times} times. Saying what they were takes this off the list.
          Nothing is posted: if they were separate conversions, book the ones not yet on the book first.
        </div>
        <TextArea label="Answer" value={why} onChange={(e) => setWhy(e.target.value)}
                  placeholder="separate conversions, booked by hand on … · re-sends of one conversion" />
      </Modal>
    </div>
  );
}

/** Whether the run's morning email went, in words; null for a run from before that was recorded. */
export function mailLine(run) {
  const mailed = run.summary?.mailed;
  if (!mailed) return null;
  return mailed.sent
    ? `Email sent to ${(mailed.to || []).join(", ")}`
    : `Email not sent: ${mailed.problem || "no reason recorded"}`;
}

function RunNow({ onStarted }) {
  const [open, setOpen] = useState(false);
  const [since, setSince] = useState("");
  const [message, setMessage] = useState("");

  const start = async () => {
    setMessage("");
    try {
      await api.post("/securities/notice-review/run/", since ? { since } : {});
      setOpen(false);
      onStarted("Reading in the background — the result arrives by email and here when it finishes.");
    } catch (e) {
      setMessage(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>Run now</Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Run the notice watch now"
             footer={<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
               <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
               <Button onClick={start}>Start</Button>
             </div>}>
        <div style={{ fontSize: 13, color: t.textSecondary, marginBottom: 10 }}>
          Reads the mail and the deal folders from this date. Leave it blank to pick up
          where the last good run stopped.
        </div>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
               style={{ padding: 8, border: `1px solid ${t.border}`, borderRadius: t.radiusSm, fontFamily: t.fontBody }} />
        {message && <div style={{ color: t.danger, fontSize: 12.5, marginTop: 10 }}>{message}</div>}
      </Modal>
    </>
  );
}

export function runLine(run) {
  const s = run.summary || {};
  const mail = s.mail || {};
  const drive = s.drive || {};
  return `${s.staged || 0} staged · ${mail.attachments || 0} attachments in ${mail.messages || 0} emails · ` +
    `${drive.changed || 0} changed files · ${s.in_register || 0} already held · ${s.not_notices || 0} not notices`;
}

export default function NoticeReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/securities/notice-review/");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = data?.pending || [];
  const runs = data?.runs || [];
  const settings = data?.settings || {};

  return (
    <div>
      <PageHeader
        title="Notice Review"
        subtitle="Conversion, exercise and equity-line notices the nightly watch found in the mail and the deal folders. None of them is on the book until you confirm it."
        action={<RunNow onStarted={(m) => { setNote(m); }} />}
      />
      {note && <div style={{ fontSize: 13, color: t.success, marginBottom: 12 }}>{note}</div>}
      {error && <div style={{ fontSize: 13, color: t.danger, marginBottom: 12 }}>{error}</div>}

      <SentAgain rows={data?.sent_again} onDone={load} />

      {loading && !data ? (
        <EmptyState message="Loading…" />
      ) : pending.length === 0 ? (
        <Card><EmptyState message="Nothing waiting. Every notice the watch has found is confirmed or rejected." /></Card>
      ) : (
        pending.map((row) => <NoticeCard key={row.notice_id} row={row} onDone={load} />)
      )}

      <Card title="Recent runs"
            subtitle={`Runs daily at ${settings.runs_daily_at_utc || "?"} UTC · reads ${(settings.mailboxes || []).join(", ") || "no mailbox"} and the deal folders · mails ${(settings.mail_to || []).join(", ")}`}>
        {runs.length === 0 ? (
          <div style={{ fontSize: 13, color: t.textMuted }}>No run yet.</div>
        ) : runs.map((run, i) => (
          <div key={i} style={{ padding: "8px 0", borderTop: i ? `1px solid ${t.borderLight}` : "none", fontSize: 12.5 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Badge text={run.ok ? "ok" : "failed"} variant={run.ok ? "green" : "red"} />
              <span style={{ color: t.text }}>{longDate(run.started_at)} · {run.trigger}</span>
              <span style={{ color: t.textMuted }}>from {longDate(run.since)}</span>
            </div>
            <div style={{ color: t.textSecondary, marginTop: 3 }}>{runLine(run)}</div>
            {mailLine(run) && (
              <div style={{ color: run.summary.mailed.sent ? t.textSecondary : t.danger, marginTop: 3 }}>{mailLine(run)}</div>
            )}
            {(run.summary?.problems || []).map((p, j) => (
              <div key={j} style={{ color: t.danger, marginTop: 3 }}>{p}</div>
            ))}
          </div>
        ))}
      </Card>
    </div>
  );
}
